import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, throwError } from 'rxjs';
import { catchError, concatMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Contract2FachbereichEntity } from '../../../entities/mdqx/contract2fachbereich.entity';
import {
    Contract2FachbereichActionTypes,
    DeleteContract2FachbereichResponse, GetContract2FachbereichResponse, PostContract2FachbereichResponse,
} from '../../actions/mdqx/contract2fachbereich.actions';
import { HttpService } from '../../../services/http.service';
import { DaveActions } from '../../actions/actions';
import { getToken } from '../../selectors/base.selectors';
import { BaseActionTypes } from '../../actions/base.actions';
import { State } from '../../index';

enum ErrorCodes {
    Get = 'Vertragszuordnungen Abrufen fehlgeschlagen',
    Post = 'Vertragszuordnung Anlegen fehlgeschlagen',
    Delete = 'Vertragszuordnung Löschen fehlgeschlagen',
}

@Injectable()
export class Contract2FachbereichEffects {
    
    get$ = createEffect(() => this.actions$.pipe(
        ofType(Contract2FachbereichActionTypes.GetRequest),
        map(
            () => `
            {
                contract2Fachbereich {
                    ${Contract2FachbereichEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        switchMap(([query, token]) =>
            this.api.graphQl({ query }, { token }).pipe(
                map(res => res && res.contract2Fachbereich),
                concatMap(res =>
                    res
                        ? [
                              new GetContract2FachbereichResponse(
                                  res.map(
                                      contract2Fachbereich =>
                                          new Contract2FachbereichEntity(contract2Fachbereich),
                                  ),
                              ),
                          ]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Get,
                        Err: err,
                        Caught: caught,
                    }
                }))),
                ),
        ),
    ));

    
    post$ = createEffect(() => this.actions$.pipe(
        ofType(Contract2FachbereichActionTypes.PostRequest),
        map(
            ({ Payload }) => `
            mutation {
                createContract2Fachbereich(
                    contractId: ${JSON.stringify(Payload.contractId)}
                    fachbereicheId: ${JSON.stringify(Payload.fachbereicheId)}
                ) {
                    ${Contract2FachbereichEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([query, token]) =>
            this.api.graphQl({ query }, { token }).pipe(
                map(res => res && res.createContract2Fachbereich),
                concatMap(res =>
                    res
                        ? [
                              new PostContract2FachbereichResponse(
                                  new Contract2FachbereichEntity(res),
                              ),
                          ]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Post,
                        Err: err,
                        Caught: caught,
                    }
                }))),
                ),
        ),
    ));

    
    delete$ = createEffect(() => this.actions$.pipe(
        ofType(Contract2FachbereichActionTypes.DeleteRequest),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([{ Payload }, token]) =>
            this.api
                .graphQl(
                    {
                        query: `
                        mutation {
                            deleteContract2Fachbereich(id: ${JSON.stringify(Payload.id)})
                        }`,
                    },
                    { token },
                )
                .pipe(
                    map(res => res && res.deleteContract2Fachbereich),
                    concatMap(res =>
                        res
                            ? [new DeleteContract2FachbereichResponse(Payload.id)]
                            : throwError('falsy response'),
                    ),
                    catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.Delete,
                            Err: err,
                            Caught: caught,
                        }
                    }))),                ),
        ),
    ));

    constructor(
        private actions$: Actions<DaveActions>,
        private api: HttpService,
        private store: Store<State>,
    ) {}
}
